import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Holiday, LeaveType, PayrollRate } from "@somasi/shared";
import {
  CARRY_OVER_RULES,
  LEAVE_PAY_TREATMENTS,
  SELF_TIME_AMENDMENTS_DEFAULT,
  leaveTypeColour,
  nextLeaveColour,
  withLeaveColours,
} from "@somasi/shared";
import {
  api,
  type HolidayDraft,
  type LeaveTypeDraft,
  type PayrollRateDraft,
} from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarX, Info, Pencil, Plus, Trash2 } from "lucide-react";
import {
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import { notify } from "@/lib/notify";
import {
  LETTERHEAD_FALLBACK,
  PayslipLetterheadFields,
  letterheadOf,
} from "@/components/payslip-letterhead";

export const Route = createFileRoute("/_authenticated/admin/payroll-settings")({
  head: () => ({
    meta: [
      { title: "Payroll settings — Somasi Ops" },
      {
        name: "description",
        content: "Statutory rates, leave types and the public holiday calendar.",
      },
    ],
  }),
  component: PayrollSettingsPage,
});

/**
 * Everything the payroll would otherwise have had compiled into it.
 *
 * Three tables: the statutory percentages, what each kind of leave costs, and
 * the shared calendar. Between them they are the whole answer to "where does
 * this number come from" — nothing in the calculation knows a percentage, a
 * ceiling, a divisor or an allowance.
 *
 * That is not tidiness. The workbook this replaces changed its employer payroll
 * tax rate twice inside one year, and a rate that needs a deploy to correct is
 * a rate that stays wrong for a month.
 */

function PayrollSettingsPage() {
  return (
    // Fills the window like a list page: the tables scroll inside their own
    // cards under pinned headings, and the rates form scrolls in its tab.
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Payroll settings"
        description="Rates, leave types and the public holiday calendar."
      />

      <Tabs defaultValue="rates" className="flex min-h-0 flex-1 flex-col gap-4">
        <TabsList className="shrink-0 self-start">
          <TabsTrigger value="rates">Rates</TabsTrigger>
          <TabsTrigger value="leave">Leave types</TabsTrigger>
          <TabsTrigger value="holidays">Holidays</TabsTrigger>
        </TabsList>

        <TabsContent value="rates" className="mt-0 min-h-0 flex-1 overflow-y-auto">
          <RatesTab />
        </TabsContent>
        <TabsContent
          value="leave"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <LeaveTypesTab />
        </TabsContent>
        <TabsContent
          value="holidays"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <HolidaysTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Rates -----------------------------------------------------------------

/**
 * The figures the business starts from, for the first save.
 *
 * Only reached when nothing is stored — the seeder writes this same record on
 * deploy — and kept here so the form opens filled in rather than blank. A blank
 * rates form is a form somebody completes from memory.
 */
const RATE_DEFAULTS: PayrollRateDraft = {
  payroll_tax_total_pct: 14,
  payroll_tax_employee_pct: 8,
  payroll_tax_annual_exemption: 10_000,
  nhi_employee_pct: 3.75,
  nhi_employer_pct: 3.75,
  nhi_period_cap: null,
  ssb_employee_pct: 4,
  ssb_employer_pct: 4.5,
  ssb_period_cap: null,
  weeks_per_year: 52,
  filing_due_days: 21,
  annual_filing_due_days: 120,
  self_time_amendments_per_month: SELF_TIME_AMENDMENTS_DEFAULT,
  payslip_company_name: LETTERHEAD_FALLBACK.name,
  payslip_company_address: LETTERHEAD_FALLBACK.address,
};

/** The stored letterhead, or the built-in one where the record has none. */
function letterheadFields(
  stored: PayrollRate,
): Pick<PayrollRateDraft, "payslip_company_name" | "payslip_company_address"> {
  const { name, address } = letterheadOf(stored);
  return { payslip_company_name: name, payslip_company_address: address };
}

function RatesTab() {
  const rates = useQuery({
    queryKey: ["payroll_rates"],
    queryFn: async () => {
      const result = await api.payrollRates.get();
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? null) as PayrollRate | null;
    },
  });

  if (rates.isLoading) {
    return <p className="text-muted-foreground py-8 text-center text-sm">Reading the rates…</p>;
  }
  if (rates.error) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>The rates could not be read</AlertTitle>
        <AlertDescription>{(rates.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  // Keyed on the stored timestamp so a save, or somebody else's save, re-seeds
  // the form from what is now on file rather than leaving the old values typed.
  return <RatesForm key={rates.data?.updated_at ?? "new"} stored={rates.data ?? null} />;
}

/**
 * The one rates record, edited in place.
 *
 * Not a list and not a dialog, because there is only ever one of these: the
 * statutory percentages are set up once and corrected when the law moves. A
 * correction applies to the next pay run and to any period recalculated after
 * it — there is no dated predecessor to fall back on, which is why the form says
 * so above the fields rather than in a tooltip.
 */
function RatesForm({ stored }: { stored: PayrollRate | null }) {
  const qc = useQueryClient();

  /*
   * A record saved before the letterhead existed holds nothing for it, and a
   * blank address box would invite somebody to type the company's address from
   * memory. The form opens on the built-in letterhead in that case, which is
   * what the payslip is printing anyway, so what is on screen is what is on the
   * document.
   */
  const baseline: PayrollRateDraft = stored
    ? { ...stored, ...letterheadFields(stored) }
    : RATE_DEFAULTS;

  const [form, setForm] = useState<PayrollRateDraft>(baseline);

  const set = <K extends keyof PayrollRateDraft>(key: K, value: PayrollRateDraft[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      const result = await api.payrollRates.save(form);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Rates saved");
      void qc.invalidateQueries({ queryKey: ["payroll_rates"] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const num = (key: keyof PayrollRateDraft, label: string, hint?: string, nullable = false) => (
    <div className="space-y-1">
      <Label htmlFor={String(key)}>{label}</Label>
      <Input
        id={String(key)}
        type="number"
        step="0.01"
        value={(form[key] as number | null) ?? ""}
        placeholder={nullable ? "No ceiling" : undefined}
        onChange={(e) =>
          set(key, (e.target.value === "" && nullable ? null : Number(e.target.value)) as never)
        }
      />
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      {stored ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>One set of rates, edited here</AlertTitle>
          <AlertDescription>
            Every pay run and every net preview reads these figures as they stand. Correcting one
            applies to the next run and to any period reopened and recalculated — the payslips
            already issued keep the amounts they were computed with.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>The rates have not been saved yet</AlertTitle>
          <AlertDescription>
            Nothing can be calculated until they are. The fields below hold the BVI figures this
            system was built against — check each one against the current law, then save.
          </AlertDescription>
        </Alert>
      )}

      <fieldset className="space-y-3 rounded-md border p-3">
        <legend className="px-1 text-sm font-semibold">Payroll tax</legend>
        {/*
         * No employer class and no ceilings to compare one against. This is a
         * Class 2 employer and stays one, so the 14% below is simply the rate —
         * stored and editable, because a rate is policy and policy moves, but
         * not a consequence of a classification anybody re-assesses here.
         */}
        <p className="text-muted-foreground text-xs">
          The Class 2 (large employer) rates. The total is split between the employee and the
          employer, and the employer carries whatever the employee's share does not cover.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {num("payroll_tax_total_pct", "Total rate (%)", "14 as a Class 2 employer")}
          {num(
            "payroll_tax_employee_pct",
            "Employee share (%)",
            "The employer carries the balance",
          )}
          {num(
            "payroll_tax_annual_exemption",
            "Annual exemption",
            "Per employee, assessed cumulatively across the year",
          )}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border p-3">
        <legend className="px-1 text-sm font-semibold">Contributions</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {num("nhi_employee_pct", "NHI employee (%)")}
          {num("nhi_employer_pct", "NHI employer (%)")}
          {num("nhi_period_cap", "NHI ceiling per period", "Leave blank for none", true)}
          <div />
          {num("ssb_employee_pct", "SSB employee (%)")}
          {num("ssb_employer_pct", "SSB employer (%)")}
          {num("ssb_period_cap", "SSB ceiling per period", "Leave blank for none", true)}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border p-3">
        <legend className="px-1 text-sm font-semibold">Day rate and deadlines</legend>
        <p className="text-muted-foreground text-xs">
          An unpaid day is a share of the working year: annual gross ÷ (weeks × the employee's own
          working days). A 48,000 salary on a five-day week gives 48,000 ÷ 260 = 184.62, and a
          three-day week a proportionally larger day. The same day is worth the same in February as
          in August, which dividing the month by its own working days would not give.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {num(
            "weeks_per_year",
            "Weeks in the working year",
            "Multiplied by each employee's working days: 52 × 5 = 260",
          )}
          {num("filing_due_days", "Return due, days after period end")}
          {num("annual_filing_due_days", "Annual return due, days after year end")}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border p-3">
        <legend className="px-1 text-sm font-semibold">Correcting clocked times</legend>
        {/*
         * The line between "fix your own timesheet" and "ask somebody". Both
         * sides of it matter: an employee who has to raise a request to correct
         * a five-minute mistake stops bothering, and a timesheet nobody
         * corrects is a timesheet nobody believes — while an employee who can
         * rewrite their hours without limit is not being clocked at all.
         */}
        <p className="text-muted-foreground text-xs">
          The clock records when somebody pressed the button, which is not always when they started
          or stopped. An employee may put their own times right this many times a calendar month;
          beyond that a correction waits for an admin to approve it. An employee&rsquo;s own record
          can override this figure, and a correction an admin makes or approves never counts against
          it.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {num(
            "self_time_amendments_per_month",
            "Self-service corrections a month",
            "Per employee. 0 means every correction needs approval.",
          )}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border p-3">
        <legend className="px-1 text-sm font-semibold">Payslip letterhead</legend>
        <p className="text-muted-foreground text-xs">
          What every payslip is issued under. Stored here rather than in the code because an address
          moves, and one that needs a deploy to correct stays wrong for a month. The same boxes are
          on the Generate payroll dialog, where somebody is more likely to notice.
        </p>
        <PayslipLetterheadFields
          value={{
            name: form.payslip_company_name ?? null,
            address: form.payslip_company_address ?? null,
          }}
          onChange={(next) =>
            setForm((prev) => ({
              ...prev,
              payslip_company_name: next.name,
              payslip_company_address: next.address,
            }))
          }
          idPrefix="rates-letterhead"
        />
      </fieldset>

      <div className="flex items-center justify-end gap-3">
        <p className="text-muted-foreground mr-auto text-xs">
          {stored ? `Last saved ${new Date(stored.updated_at).toLocaleString()}` : "Never saved"}
        </p>
        <Button
          variant="outline"
          onClick={() => setForm(baseline)}
          disabled={!dirty || save.isPending}
        >
          Discard changes
        </Button>
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save rates"}
        </Button>
      </div>
    </div>
  );
}

// ---- Leave types -----------------------------------------------------------

const TREATMENT_LABEL: Record<string, string> = {
  full: "Fully paid",
  partial: "Partly paid",
  unpaid: "Unpaid",
};

function LeaveTypesTab() {
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [creating, setCreating] = useState(false);

  const types = useQuery({
    queryKey: ["leave_types_all"],
    queryFn: async () => {
      const result = await api.leaveTypes.list().order("sort_order").all();
      if (result.error) throw new Error(result.error.message);
      /*
       * Colours resolved here, once, rather than at each swatch: a type with
       * none of its own gets one nothing else is using, so the legend never
       * shows two kinds of leave as the same square.
       */
      return withLeaveColours((result.data ?? []) as LeaveType[]);
    },
  });

  const remove = useMutation({
    mutationFn: async (type: LeaveType) => {
      const result = await api.leaveTypes.remove(type.id);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Leave type removed");
      void qc.invalidateQueries({ queryKey: ["leave_types_all"] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New leave type
        </Button>
      </div>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead className="text-center">Draws allowance</TableHead>
              <TableHead className="text-center">Counts as absence</TableHead>
              <TableHead className="text-right">Default days</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.data?.map((type) => (
              <TableRow key={type.id} className={type.active ? "" : "opacity-50"}>
                <TableCell>
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded text-xs font-semibold text-white"
                    style={{ backgroundColor: leaveTypeColour(type) }}
                  >
                    {type.code}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{type.name}</TableCell>
                <TableCell>
                  {TREATMENT_LABEL[type.pay_treatment]}
                  {type.pay_treatment === "partial" && ` (${type.paid_percentage ?? 0}%)`}
                  {type.paid_days_limit != null && (
                    <span className="text-muted-foreground block text-xs">
                      first {type.paid_days_limit} day(s) only
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {type.consumes_allowance ? "Yes" : "—"}
                </TableCell>
                <TableCell className="text-center">
                  {type.counts_as_absence ? "Yes" : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {type.default_allowance_days ?? "—"}
                </TableCell>
                <TableCell>
                  <RowActions>
                    <RowAction label="Edit" onClick={() => setEditing(type)}>
                      <Pencil />
                    </RowAction>
                    <RowAction
                      label="Delete"
                      destructive
                      onClick={async () => {
                        const ok = await confirmDelete(
                          type.name,
                          "A type that any leave request or allowance names cannot be deleted — " +
                            "set it inactive instead, which keeps every day already recorded readable.",
                        );
                        if (ok) remove.mutate(type);
                      }}
                    >
                      <Trash2 />
                    </RowAction>
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListPageTable>

      {(creating || editing) && (
        <LeaveTypeDialog
          type={editing}
          allTypes={types.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["leave_types_all"] });
          }}
        />
      )}
    </div>
  );
}

function LeaveTypeDialog({
  type,
  allTypes,
  onClose,
  onSaved,
}: {
  type: LeaveType | null;
  allTypes: LeaveType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<LeaveTypeDraft>(
    type ?? {
      code: "",
      name: "",
      colour: nextLeaveColour(allTypes),
      pay_treatment: "full",
      consumes_allowance: true,
      counts_as_absence: true,
      carry_over_rule: "none",
      allow_negative_balance: false,
      requires_approval: true,
      self_bookable: true,
      active: true,
      sort_order: 100,
    },
  );

  const set = <K extends keyof LeaveTypeDraft>(key: K, value: LeaveTypeDraft[K]) =>
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Clearing the limit clears where its overflow went, so a type cannot be
      // stored pointing at a conversion it no longer performs.
      if (key === "paid_days_limit" && value == null) next.beyond_limit_type_id = null;
      return next;
    });

  /**
   * The type the days past a limit become.
   *
   * It is not asked: a limit on pay means the rest is unpaid, so the unpaid
   * type is it. Active matters — an inactive type would still be written onto
   * real days and deducted from real pay, which is not what marking one inactive is
   * meant to mean.
   *
   * A type already pointing somewhere else keeps that destination. Half pay
   * for sickness and unpaid after that is a real arrangement, and flattening
   * it to unpaid would quietly change somebody's pay.
   */
  const unpaidType = allTypes.find(
    (t) => t.active && t.pay_treatment === "unpaid" && t.id !== type?.id,
  );

  const save = useMutation({
    mutationFn: async () => {
      let draft = form;

      /*
       * "Stop paying after 12" already says the 13th day is unpaid, so it is
       * not also asked what unpaid means. If nothing unpaid exists yet it is
       * created here — the alternative is a save refused by a server rule the
       * form never mentioned.
       */
      if (draft.paid_days_limit != null && !draft.beyond_limit_type_id) {
        let destinationId = unpaidType?.id;
        if (!destinationId) {
          const created = await api.leaveTypes.create({
            code: "U",
            name: "Unpaid Leave",
            colour: "#64748b",
            pay_treatment: "unpaid",
            consumes_allowance: false,
            counts_as_absence: true,
            carry_over_rule: "none",
            allow_negative_balance: true,
            requires_approval: true,
            self_bookable: false,
            active: true,
            sort_order: 90,
          });
          if (created.error) throw new Error(created.error.message);
          destinationId = created.data?.id;
        }
        draft = { ...draft, beyond_limit_type_id: destinationId };
      }

      const result = type
        ? await api.leaveTypes.update(type.id, draft)
        : await api.leaveTypes.create(draft);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Leave type saved");
      onSaved();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{type ? type.name : "New leave type"}</DialogTitle>
          <DialogDescription>
            The flags here are what the payroll reads. Nothing about a leave type is special-cased
            in code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="code" required>
                Code
              </Label>
              <Input
                id="code"
                value={form.code ?? ""}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                maxLength={4}
                placeholder="L"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="name" required>
                Name
              </Label>
              <Input
                id="name"
                value={form.name ?? ""}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="colour">Colour on the grid</Label>
              <Input
                id="colour"
                type="color"
                value={form.colour ?? "#16a34a"}
                onChange={(e) => set("colour", e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1">
              <Label>Pay treatment</Label>
              <Select
                value={form.pay_treatment ?? "full"}
                onValueChange={(v) => set("pay_treatment", v as never)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_PAY_TREATMENTS.map((treatment) => (
                    <SelectItem key={treatment} value={treatment}>
                      {TREATMENT_LABEL[treatment]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.pay_treatment === "partial" && (
              <div className="space-y-1">
                <Label htmlFor="paid_percentage" required>
                  Paid (%)
                </Label>
                <Input
                  id="paid_percentage"
                  type="number"
                  value={form.paid_percentage ?? ""}
                  onChange={(e) => set("paid_percentage", Number(e.target.value))}
                />
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="default_allowance_days">Days a year</Label>
              <Input
                id="default_allowance_days"
                type="number"
                step="0.5"
                value={form.default_allowance_days ?? ""}
                onChange={(e) =>
                  set(
                    "default_allowance_days",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="None"
              />
              <p className="text-muted-foreground text-xs">
                How much of this everybody gets. Override it per person on the Leave page — 17, 20
                and 24 are all in use.
              </p>
            </div>

            <div className="space-y-1">
              <Label>Carry over unused days</Label>
              <Select
                value={form.carry_over_rule ?? "none"}
                onValueChange={(v) => set("carry_over_rule", v as never)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARRY_OVER_RULES.map((rule) => (
                    <SelectItem key={rule} value={rule}>
                      {rule === "none"
                        ? "Not at all"
                        : rule === "capped"
                          ? "Up to a cap"
                          : "All of it"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.carry_over_rule === "capped" && (
              <div className="space-y-1">
                <Label htmlFor="carry_over_max_days">Carry-over cap (days)</Label>
                <Input
                  id="carry_over_max_days"
                  type="number"
                  step="0.5"
                  value={form.carry_over_max_days ?? ""}
                  onChange={(e) => set("carry_over_max_days", Number(e.target.value))}
                />
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="paid_days_limit">Stop paying after (optional)</Label>
              <Input
                id="paid_days_limit"
                type="number"
                step="0.5"
                value={form.paid_days_limit ?? ""}
                onChange={(e) =>
                  set("paid_days_limit", e.target.value === "" ? null : Number(e.target.value))
                }
                placeholder="No limit"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={
              save.isPending ||
              !form.code?.trim() ||
              !form.name?.trim() ||
              (form.pay_treatment === "partial" && form.paid_percentage == null)
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Holidays --------------------------------------------------------------

function HolidaysTab() {
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<HolidayDraft>({
    territory: "VG",
    date: new Date().toISOString().slice(0, 10),
    name: "",
  });

  const sort = useListSort({ column: "date", ascending: true });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  const filters = { query, sort: sort.sort };
  const pager = usePagination({ resetKey: filters });

  /**
   * One page of the calendar, filtered and sorted in the API.
   *
   * This read used to be `list().order("date")` with no page at all, and a list
   * endpoint answers with one default page when no limit is sent — so the tab
   * silently showed the first ten dates and dropped the rest. A year is fifteen
   * or sixteen public holidays, which means everything from the autumn onwards
   * was missing with nothing on screen saying so. The footer now states the
   * total, so a cut-off list cannot look like the whole calendar again.
   */
  const holidays = useQuery({
    queryKey: ["holidays", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const list = api.holidays.list();

      /*
       * The date is searched as text alongside the name, which is what makes a
       * year filter unnecessary: the column is stored as `YYYY-MM-DD`, so "2026"
       * narrows to that year and "2026-12" to that December, and neither needs a
       * second read to discover which years the table holds.
       */
      if (query) {
        list.or([
          { column: "name", op: "ilike", value: `%${query}%` },
          { column: "date", op: "ilike", value: `%${query}%` },
          { column: "territory", op: "ilike", value: `%${query}%` },
        ]);
      }

      list.order(sort.sort.column, { ascending: sort.sort.ascending });
      // Two holidays can share a name across years — Christmas Day every year —
      // so a name sort without a tie-break would order them differently between
      // requests, and the same row could appear on two pages or on neither.
      if (sort.sort.column !== "date") list.order("date");

      const { data, error, count } = await list.page(pager.page, pager.pageSize);
      if (error) throw new Error(error.message);
      return { rows: (data ?? []) as Holiday[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const rows = holidays.data?.rows ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const result = await api.holidays.create(form);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Holiday added");
      setAdding(false);
      setForm({ ...form, name: "" });
      void qc.invalidateQueries({ queryKey: ["holidays"] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const remove = useMutation({
    mutationFn: async (holiday: Holiday) => {
      const result = await api.holidays.remove(holiday.id);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Holiday removed");
      void qc.invalidateQueries({ queryKey: ["holidays"] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <Alert className="shrink-0">
        <Info className="h-4 w-4" />
        <AlertTitle>A date here settles that day for everybody</AlertTitle>
        <AlertDescription>
          Which is also what stops a public holiday falling inside a booked week of leave from
          consuming a day of somebody's allowance. Several BVI holidays move year to year, so next
          year's dates are added here rather than assumed.
        </AlertDescription>
      </Alert>

      <ListToolbar className="mb-0 shrink-0">
        <ListSearch
          value={search}
          onChange={setSearch}
          placeholder="Search holiday, date or territory…"
        />
        <Button onClick={() => setAdding(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add holiday
        </Button>
      </ListToolbar>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <SortHeader sort={sort} column="date">
                Date
              </SortHeader>
              <SortHeader sort={sort} column="name">
                Name
              </SortHeader>
              <SortHeader sort={sort} column="territory">
                Territory
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holidays.isLoading ? (
              <TableMessageRow colSpan={4}>Loading…</TableMessageRow>
            ) : holidays.isError ? (
              <TableMessageRow colSpan={4} tone="error">
                <CalendarX className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {holidays.error.message}
              </TableMessageRow>
            ) : rows.length === 0 ? (
              <TableMessageRow colSpan={4}>
                <CalendarX className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {query ? "No holidays match." : "No holidays yet."}
              </TableMessageRow>
            ) : (
              rows.map((holiday) => (
                <TableRow key={holiday.id}>
                  <TableCell className="whitespace-nowrap tabular-nums">{holiday.date}</TableCell>
                  <TableCell className="font-medium">{holiday.name}</TableCell>
                  <TableCell>{holiday.territory}</TableCell>
                  <TableCell>
                    <RowActions>
                      <RowAction
                        label="Delete"
                        destructive
                        onClick={async () => {
                          const ok = await confirmDelete(`${holiday.name} (${holiday.date})`);
                          if (ok) remove.mutate(holiday);
                        }}
                      >
                        <Trash2 />
                      </RowAction>
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          pagination={pager}
          total={holidays.data?.total}
          loading={holidays.isLoading}
          unit="holiday"
        />
      </ListPageTable>

      {adding && (
        <Dialog open onOpenChange={(open) => !open && setAdding(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a public holiday</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="holiday-date" required>
                  Date
                </Label>
                <Input
                  id="holiday-date"
                  type="date"
                  value={form.date ?? ""}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="holiday-name" required>
                  Name
                </Label>
                <Input
                  id="holiday-name"
                  value={form.name ?? ""}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="holiday-territory" required>
                  Territory
                </Label>
                <Input
                  id="holiday-territory"
                  value={form.territory ?? ""}
                  onChange={(e) => setForm({ ...form, territory: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name?.trim()}>
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
